import { CreditCard, AlertCircle, Clock, FileText, ExternalLink } from 'lucide-react';
import { InfoSection, InfoField } from '@/core/components/ui/detail-blade';
import { Alert, AlertDescription, AlertTitle } from '@/core/components/ui/alert';
import { Button } from '@/core/components/ui/button';
import { KioskSessionGridRow, KioskSessionDetail } from '../types/kiosk-session-types';

interface PaymentInfoSectionProps {
  session: KioskSessionGridRow | null;
  detail: KioskSessionDetail | undefined;
  fallbackCurrency?: string;
}

function isValidCurrencyCode(code: string | undefined): code is string {
  if (!code || !/^[A-Z]{3}$/.test(code)) return false;
  try {
    new Intl.NumberFormat('en', { style: 'currency', currency: code });
    return true;
  } catch { return false; }
}

export function PaymentInfoSection({ session, detail, fallbackCurrency }: PaymentInfoSectionProps) {
  if (!detail) {
    return null;
  }

  // Format payment method for display
  // Payment method is already mapped in the transformer (1 -> Credit Card, 2 -> Prepaid)
  const formatPaymentMethod = (method?: string): string => {
    if (!method || method === 'Unknown') return '';
    return method;
  };

  const formatCurrency = (value?: number): string => {
    if (value == null || isNaN(value)) return '';
    const rawCode = detail.currency || session?.currency;
    const safeCurrency = isValidCurrencyCode(rawCode) ? rawCode
      : isValidCurrencyCode(fallbackCurrency) ? fallbackCurrency
      : null;
    if (!safeCurrency) {
      return new Intl.NumberFormat('en', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
    return new Intl.NumberFormat('en-IL', {
      style: 'currency',
      currency: safeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format date for display
  const formatDateTime = (date?: Date): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Check for settlement difference (when payment amount differs from calculated cost)
  const hasDifference = detail.settAmount != null && detail.totalIncVat != null &&
    Math.abs(detail.settAmount - detail.totalIncVat) > 0.01;

  return (
    <InfoSection
      title="Payment Transaction"
      icon={CreditCard}
      collapsible={true}
      defaultExpanded={true}
      storageKey="kioskSessionDetail.paymentInfo.expanded"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left column: Authorization & Settlement */}
        <div className="space-y-3">
          <InfoField
            label="Authorization Amount"
            value={formatCurrency(detail.authAmount)}
          />

          <InfoField
            label="Authorization Time"
            value={formatDateTime(detail.authTime)}
            icon={Clock}
          />

          <InfoField
            label="Settlement Amount"
            value={formatCurrency(detail.settAmount || detail.settlement_value)}
          />

          <InfoField
            label="Settlement Time"
            value={formatDateTime(detail.settTime)}
            icon={Clock}
          />
        </div>

        {/* Right column: Payment Details */}
        <div className="space-y-3">
          <InfoField
            label="Payment Method"
            value={formatPaymentMethod(detail.paymentMethod)}
          />

          <InfoField
            label="Card Identifier"
            value={detail.cardIdentifier ? `****${detail.cardIdentifier}` : ''}
            type="id"
          />

          <InfoField
            label="Transaction ID"
            value={detail.payment_transaction_id || session?.payment_transaction_id}
            type="id"
            copyable
          />

          <InfoField
            label="Machine Name"
            value={detail.machineName || session?.machine_name}
          />
        </div>
      </div>

      {/* Receipt link */}
      {(detail.receipt || session?.receipt) && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <span className="text-sm text-muted-foreground">Receipt</span>
          <Button
            variant="outline"
            size="sm"
            asChild
          >
            <a
              href={detail.receipt || session?.receipt}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1"
            >
              <FileText className="h-4 w-4" />
              View Receipt
              <ExternalLink className="h-3 w-3" />
            </a>
          </Button>
        </div>
      )}

      {/* Settlement difference alert */}
      {hasDifference && (
        <Alert variant="default" className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Settlement Difference Detected</AlertTitle>
          <AlertDescription>
            Settlement amount ({formatCurrency(detail.settAmount)}) differs from
            calculated charging cost ({formatCurrency(detail.totalIncVat)}). This may
            indicate discounts, adjustments, or promotional pricing applied.
          </AlertDescription>
        </Alert>
      )}
    </InfoSection>
  );
}